// src/pages/ResetPasswordPage.tsx
// VERSÃO GRAIA - Com identidade visual completa

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Lock } from 'lucide-react';
import './AuthPagesGRAIA.css';

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        if (newPassword.length < 6) {
            setError('A senha deve ter no mínimo 6 caracteres');
            return;
        }

        if (!token) {
            setError('Token inválido');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
            } else {
                setError(data.message || 'Erro ao redefinir senha');
            }
        } catch (err) {
            setError('Erro ao conectar ao servidor');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="graia-auth-wrapper">
                <div className="graia-auth-identity">
                    <div className="graia-auth-identity-content">
                        <div className="graia-auth-logo-section">
                            <img
                                src="/graia-logo.png"
                                alt="GRAIA Logo"
                                className="graia-auth-logo-img"
                            />
                            <h1 className="graia-auth-brand">GRAIA</h1>
                            <p className="graia-auth-tagline">Estudo estruturado. Conhecimento consolidado.</p>
                        </div>
                    </div>
                </div>

                <div className="graia-auth-form-area">
                    <div className="graia-auth-form-container">
                        <div className="graia-auth-card">
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '20px',
                                color: 'var(--graia-auth-success)'
                            }}>
                                <CheckCircle size={48} strokeWidth={1.5} />
                            </div>

                            <div className="graia-auth-header">
                                <h2>Senha redefinida</h2>
                                <p style={{ marginTop: '12px', lineHeight: '1.5' }}>
                                    Sua senha foi alterada com sucesso! Agora você pode fazer login com a nova senha.
                                </p>
                            </div>

                            <button
                                className="graia-auth-btn-primary"
                                onClick={() => navigate('/login')}
                            >
                                Fazer login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="graia-auth-wrapper">
            {/* COLUNA ESQUERDA - IDENTIDADE */}
            <div className="graia-auth-identity">
                <div className="graia-auth-identity-content">
                    <div className="graia-auth-logo-section">
                        <img
                            src="/graia-logo.png"
                            alt="GRAIA Logo"
                            className="graia-auth-logo-img"
                        />
                        <h1 className="graia-auth-brand">GRAIA</h1>
                        <p className="graia-auth-tagline">Estudo estruturado. Conhecimento consolidado.</p>
                    </div>
                </div>
            </div>

            {/* COLUNA DIREITA - FORMULÁRIO */}
            <div className="graia-auth-form-area">
                <div className="graia-auth-form-container">
                    <div className="graia-auth-card">
                        <div className="graia-auth-header">
                            <h2>Redefinir senha</h2>
                            <p style={{ marginTop: '8px', lineHeight: '1.5' }}>
                                Digite sua nova senha
                            </p>
                        </div>

                        {error && (
                            <div className="graia-auth-alert graia-auth-alert-error">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="graia-auth-form">
                            <div className="graia-auth-field">
                                <label className="graia-auth-label">Nova senha</label>
                                <div className="graia-auth-input-wrapper">
                                    <Lock size={16} className="graia-auth-input-icon" />
                                    <input
                                        type="password"
                                        className="graia-auth-input"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Mínimo 6 caracteres"
                                        required
                                        disabled={loading}
                                        minLength={6}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className="graia-auth-field">
                                <label className="graia-auth-label">Confirmar nova senha</label>
                                <div className="graia-auth-input-wrapper">
                                    <Lock size={16} className="graia-auth-input-icon" />
                                    <input
                                        type="password"
                                        className="graia-auth-input"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Digite a senha novamente"
                                        required
                                        disabled={loading}
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="graia-auth-btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Redefinindo...' : 'Redefinir senha'}
                            </button>
                        </form>

                        <div className="graia-auth-footer">
                            <p>
                                Lembrou a senha?{' '}
                                <button
                                    onClick={() => navigate('/login')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--graia-auth-primary)',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        padding: 0,
                                        textDecoration: 'none',
                                        font: 'inherit'
                                    }}
                                >
                                    Voltar ao login
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;